import React, { useEffect } from 'react';
import { VALIDATION_NAVIGATION_EVENT } from '../utils/formFeedback';

const ERROR_SELECTOR = [
  '.alert-error',
  '.payment-draft-error',
  '.form-group .error',
  '[data-error-focus="true"]',
].join(', ');

function firstErrorInside(node: Node): HTMLElement | null {
  if (!(node instanceof Element)) return null;
  if (node.matches(ERROR_SELECTOR)) return node as HTMLElement;
  return node.querySelector(ERROR_SELECTOR) as HTMLElement | null;
}

function revealError(target: HTMLElement) {
  if (!document.contains(target)) return;
  if (target.getAttribute('aria-hidden') === 'true' || target.hasAttribute('hidden')) return;

  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  if (typeof (target as any).scrollIntoView === 'function') {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
  if (typeof target.focus === 'function') {
    target.focus({ preventScroll: true });
  }
}

const ErrorFocusManager: React.FC = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.body) return;

    let specificNavigationUntil = 0;
    let pendingTimer: number | null = null;
    let pendingTarget: HTMLElement | null = null;

    const onSpecificNavigation = () => {
      specificNavigationUntil = Date.now() + 350;
      if (pendingTimer !== null) {
        window.clearTimeout(pendingTimer);
        pendingTimer = null;
        pendingTarget = null;
      }
    };

    const schedule = (target: HTMLElement) => {
      pendingTarget = target;
      if (pendingTimer !== null) window.clearTimeout(pendingTimer);

      pendingTimer = window.setTimeout(() => {
        pendingTimer = null;
        if (Date.now() < specificNavigationUntil) return;
        if (!pendingTarget) return;
        revealError(pendingTarget);
        pendingTarget = null;
      }, 80);
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (parent) {
            const target = parent.matches(ERROR_SELECTOR)
              ? parent as HTMLElement
              : parent.closest(ERROR_SELECTOR) as HTMLElement | null;
            if (target) {
              schedule(target);
              return;
            }
          }
        }

        for (const node of Array.from(mutation.addedNodes)) {
          const target = firstErrorInside(node);
          if (target) {
            schedule(target);
            return;
          }
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          const target = mutation.target.matches(ERROR_SELECTOR)
            ? mutation.target
            : mutation.target.querySelector(ERROR_SELECTOR) as HTMLElement | null;
          if (target) {
            schedule(target);
            return;
          }
        }
      }
    });

    window.addEventListener(VALIDATION_NAVIGATION_EVENT, onSpecificNavigation);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'hidden', 'aria-hidden'],
    });

    return () => {
      observer.disconnect();
      window.removeEventListener(VALIDATION_NAVIGATION_EVENT, onSpecificNavigation);
      if (pendingTimer !== null) window.clearTimeout(pendingTimer);
    };
  }, []);

  return null;
};

export default ErrorFocusManager;
